import React, { useRef, useState, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Trash2,
  Send,
  Save,
  Eye,
  Palette,
  ALargeSmall,
  AlignRight,
  AlignJustify,
  AlignCenter,
  AlignLeft,
  File as FileIcon
} from "lucide-react";

import "./RichTextEditor.css";
import { toast } from "react-toastify";
import axios from "axios";
import { uploadOneFiles } from "../../fileUploader/SingleFileUploader.js";

type ImageAlign = "left" | "center" | "right";

const getImageWrapperWidth = (wrapper: HTMLElement, img: HTMLImageElement): number => {
  const wrapperWidth = parseInt(wrapper.style.width, 10);
  if (!isNaN(wrapperWidth) && wrapperWidth > 0) return wrapperWidth;

  const imgWidth = parseInt(img.getAttribute("width") || img.style.width, 10);
  if (!isNaN(imgWidth) && imgWidth > 0) return imgWidth;

  return img.naturalWidth || 300;
};

const createResizableImageMarkup = (src: string, width = 300) => `
  <div class="resizable-image" contenteditable="false" data-align="left" style="width:${width}px;">
    <img src="${src}" alt="" draggable="false" />
    <span class="resize-handle" contenteditable="false"></span>
  </div>
`;

const placeCaretAtEnd = (el: HTMLElement) => {
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
};

const wrapImgElement = (img: HTMLImageElement): HTMLDivElement => {
  const wrapper = document.createElement("div");
  wrapper.className = "resizable-image";
  wrapper.contentEditable = "false";
  wrapper.setAttribute("data-align", "left");

  const width = parseInt(img.getAttribute("width") || img.style.width, 10);
  wrapper.style.width = `${!isNaN(width) && width > 0 ? width : 300}px`;

  const newImg = document.createElement("img");
  newImg.src = img.getAttribute("src") || "";
  newImg.alt = img.getAttribute("alt") || "";
  newImg.draggable = false;

  const handle = document.createElement("span");
  handle.className = "resize-handle";
  handle.contentEditable = "false";

  wrapper.appendChild(newImg);
  wrapper.appendChild(handle);
  return wrapper;
};

const base64ToFile = (base64: string, filename: string) => {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export default function RichTextEditor({ restaurantName }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const selectedImageRef = useRef<HTMLElement | null>(null);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  // Images
  const [images, setImages] = useState<{ src: string; name: string }[]>([]);

  // Documents
  const [documents, setDocuments] = useState<{ name: string; url: string }[]>([]);

  // --- Link popup ---
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkTargetBlank, setLinkTargetBlank] = useState(true);
  const savedRange = useRef<Range | null>(null);

  // --- Color popup ---
  const [showColorPopup, setShowColorPopup] = useState(false);
  const [colorValue, setColorValue] = useState("#ff0000");

  // --- Font popup ---
  const [showFontPopup, setShowFontPopup] = useState(false);
  const [fontSizeValue, setFontSizeValue] = useState("16");
  const [fontFamilyValue, setFontFamilyValue] = useState("Arial");

  // --- Formatting ---
  const format = (command: string, value: any = null) => {
    document.execCommand(command, false, value);
    setContent(editorRef.current?.innerHTML || "");
  };

  const handleInput = () => {
    setContent(editorRef.current?.innerHTML || "");
  };

  const clearSelectedImage = () => {
    editorRef.current
      ?.querySelectorAll(".resizable-image.is-selected")
      .forEach((el) => el.classList.remove("is-selected"));
    selectedImageRef.current = null;
  };

  const ensureEditableTail = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const lastChild = editor.lastChild;
    const lastIsImage =
      lastChild instanceof HTMLElement &&
      lastChild.classList.contains("resizable-image");

    if (!lastChild || lastIsImage) {
      editor.appendChild(document.createElement("br"));
    }
  };

  const removeSelectedImage = () => {
    const wrapper = selectedImageRef.current;
    if (!wrapper || !editorRef.current) return;

    wrapper.remove();
    selectedImageRef.current = null;
    clearSelectedImage();
    ensureEditableTail();
    placeCaretAtEnd(editorRef.current);
    setContent(editorRef.current.innerHTML);
  };

  const insertResizableImage = (src: string, width?: number) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    const editorWidth = editor.clientWidth || 600;
    const imgHTML =
      createResizableImageMarkup(
        src,
        width ?? Math.min(300, editorWidth - 24)
      ) + "<br>";

    document.execCommand("insertHTML", false, imgHTML);
    ensureEditableTail();
    placeCaretAtEnd(editor);
    setContent(editor.innerHTML);
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    const target = e.target as HTMLElement;
    const wrapper = target.closest(".resizable-image") as HTMLElement | null;

    if (wrapper && editor.contains(wrapper)) {
      clearSelectedImage();
      wrapper.classList.add("is-selected");
      selectedImageRef.current = wrapper;
      return;
    }

    clearSelectedImage();
    ensureEditableTail();

    const hasOnlyImages =
      editor.querySelector(".resizable-image") &&
      [...editor.childNodes].every(
        (node) =>
          node.nodeName === "BR" ||
          (node instanceof HTMLElement &&
            node.classList.contains("resizable-image"))
      );

    if (hasOnlyImages || target === editor) {
      placeCaretAtEnd(editor);
    }
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const editor = editorRef.current;
    if (!editor) return;

    if (e.key === "Backspace" || e.key === "Delete") {
      if (selectedImageRef.current) {
        e.preventDefault();
        removeSelectedImage();
        return;
      }
    }

    const isPrintable =
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

    if (!isPrintable) return;

    if (selectedImageRef.current) {
      clearSelectedImage();
    }

    const sel = window.getSelection();
    const caretInEditor =
      sel &&
      sel.rangeCount > 0 &&
      editor.contains(sel.getRangeAt(0).commonAncestorContainer);

    if (!caretInEditor) {
      e.preventDefault();
      ensureEditableTail();
      placeCaretAtEnd(editor);
      document.execCommand("insertText", false, e.key);
      setContent(editor.innerHTML);
    }
  };

  const alignSelectedImage = (align: ImageAlign) => {
    const wrapper = selectedImageRef.current;
    if (!wrapper) {
      toast.info("Click an image first to change alignment");
      return;
    }

    wrapper.setAttribute("data-align", align);
    setContent(editorRef.current?.innerHTML || "");
  };

  // --- Handle Paste (for screenshots/images) ---
  // const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
  //   const items = e.clipboardData?.items;
  //   if (!items) return;

  //   for (let i = 0; i < items.length; i++) {
  //     if (items[i].type.indexOf("image") !== -1) {
  //       e.preventDefault();
  //       const blob = items[i].getAsFile();
  //       if (!blob) continue;

  //       const reader = new FileReader();
  //       reader.onload = (event: ProgressEvent<FileReader>) => {
  //         if (event.target?.result) {
  //           const imgId = `img-${Date.now()}-${Math.random()}`;
  //           const imgWrapper = `
  //             <span class="pasted-image-wrapper" style="display: inline-block; position: relative; margin: 8px 4px;">
  //               <img src="${event.target.result}" style="max-width: 150px; max-height: 150px; display: block; border: 1px solid #ddd; border-radius: 4px;" data-img-id="${imgId}" />
  //               <button class="delete-pasted-img" data-img-id="${imgId}" style="position: absolute; top: -8px; right: -8px; background: #ff4444; color: white; border: none; border-radius: 50%; width: 24px; height: 24px; cursor: pointer; font-size: 16px; line-height: 1; box-shadow: 0 2px 4px rgba(0,0,0,0.2); font-weight: bold;">×</button>
  //             </span>
  //           `;
  //           document.execCommand("insertHTML", false, imgWrapper);
  //           setContent(editorRef.current?.innerHTML || "");

  //           // Attach delete handler
  //           setTimeout(() => {
  //             const deleteBtn = editorRef.current?.querySelector(
  //               `button[data-img-id="${imgId}"]`
  //             );
  //             if (deleteBtn) {
  //               deleteBtn.addEventListener("click", (evt) => {
  //                 evt.preventDefault();
  //                 evt.stopPropagation();
  //                 const wrapper = (evt.target as HTMLElement).closest(".pasted-image-wrapper");
  //                 wrapper?.remove();
  //                 setContent(editorRef.current?.innerHTML || "");
  //               });
  //             }
  //           }, 100);
  //         }
  //       };
  //       reader.readAsDataURL(blob);
  //       break;
  //     }
  //   }
  // };

  // const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
  //   e.preventDefault();

  //   const clipboard = e.clipboardData;
  //   if (!clipboard) return;

  //   // 1️⃣ Handle direct image paste (like screenshot)
  //   const items = clipboard.items;
  //   for (let i = 0; i < items.length; i++) {
  //     if (items[i].type.startsWith("image")) {
  //       const file = items[i].getAsFile();
  //       if (!file) continue;

  //       const reader = new FileReader();
  //       reader.onload = (event) => {
  //         if (!event.target?.result) return;

  //         const imgHTML = `
  //           <img 
  //             src="${event.target.result}" 
  //             style="max-width:100%;height:auto;border-radius:4px;margin:6px 0;" 
  //           />
  //         `;

  //         document.execCommand("insertHTML", false, imgHTML);
  //         setContent(editorRef.current?.innerHTML || "");
  //       };

  //       reader.readAsDataURL(file);
  //       return;
  //     }
  //   }

  //   // 2️⃣ Handle HTML content (from Google, websites, etc.)
  //   const htmlData = clipboard.getData("text/html");

  //   if (htmlData) {
  //     const parser = new DOMParser();
  //     const doc = parser.parseFromString(htmlData, "text/html");

  //     // Remove dangerous or layout-breaking tags
  //     doc.querySelectorAll("style, script, meta, link").forEach(el => el.remove());

  //     // Remove unwanted tags
  //     // doc.querySelectorAll("style, script, meta, link, hr, table, thead, tbody, tr, td").forEach(el => el.remove());

  //     // // Remove empty divs
  //     // doc.querySelectorAll("div").forEach(div => {
  //     //   if (!div.textContent?.trim() && div.querySelectorAll("img").length === 0) {
  //     //     div.remove();
  //     //   }
  //     // });

  //     // // Strip all attributes
  //     // doc.querySelectorAll("*").forEach(el => {
  //     //   [...el.attributes].forEach(attr => el.removeAttribute(attr.name));
  //     // });

  //     // Remove inline styles that break layout
  //     doc.querySelectorAll("*").forEach(el => {
  //       el.removeAttribute("style");
  //       el.removeAttribute("class");
  //     });

  //     // Force images to be responsive
  //     doc.querySelectorAll("img").forEach(img => {
  //       img.style.maxWidth = "100%";
  //       img.style.height = "auto";
  //     });

  //     const cleanHTML = doc.body.innerHTML;

  //     document.execCommand("insertHTML", false, cleanHTML);
  //     setContent(editorRef.current?.innerHTML || "");
  //     return;
  //   }

  //   // 3️⃣ Fallback to plain text
  //   const text = clipboard.getData("text/plain");
  //   document.execCommand("insertText", false, text);
  // };


  // const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
  //   e.preventDefault();

  //   const clipboard = e.clipboardData;
  //   if (!clipboard) return;

  //   // 1️⃣ Handle direct image paste (like screenshot)
  //   const items = clipboard.items;
  //   for (let i = 0; i < items.length; i++) {
  //     if (items[i].type.startsWith("image")) {
  //       const file = items[i].getAsFile();
  //       if (!file) continue;

  //       const reader = new FileReader();
  //       reader.onload = (event) => {
  //         if (!event.target?.result) return;

  //         const imgHTML = `<img src="${event.target.result}" />`;

  //         document.execCommand("insertHTML", false, imgHTML);
  //         setContent(editorRef.current?.innerHTML || "");
  //       };

  //       reader.readAsDataURL(file);
  //       return;
  //     }
  //   }

  //   // 2️⃣ Handle HTML paste (from websites)
  //   const html = clipboard.getData("text/html");

  //   if (html) {
  //     const parser = new DOMParser();
  //     const doc = parser.parseFromString(html, "text/html");

  //     // Allowed tags only
  //     const allowedTags = [
  //       "DIV", "P", "BR", "B", "I", "STRONG", "EM",
  //       "UL", "OL", "LI", "A", "IMG",
  //       "TABLE", "TBODY", "TR", "TD"
  //     ];

  //     const cleanNode = (node: any): any => {
  //       if (node.nodeType === 3) {
  //         return document.createTextNode(node.textContent);
  //       }

  //       if (!allowedTags.includes(node.nodeName)) {
  //         const fragment = document.createDocumentFragment();
  //         node.childNodes.forEach((child: any) => {
  //           fragment.appendChild(cleanNode(child));
  //         });
  //         return fragment;
  //       }

  //       const el = document.createElement(node.nodeName);

  //       if (node.nodeName === "A") {
  //         el.setAttribute("href", node.getAttribute("href") || "#");
  //       }

  //       if (node.nodeName === "IMG") {
  //         el.setAttribute("src", node.getAttribute("src") || "");
  //         el.setAttribute("style", node.getAttribute("style") || "");
  //         el.setAttribute("width", node.getAttribute("width") || "");
  //       }

  //       node.childNodes.forEach((child: any) => {
  //         el.appendChild(cleanNode(child));
  //       });

  //       return el;
  //     };

  //     const cleaned = cleanNode(doc.body);

  //     const container = document.createElement("div");
  //     container.appendChild(cleaned);

  //     document.execCommand("insertHTML", false, container.innerHTML);
  //     setContent(editorRef.current?.innerHTML || "");
  //     return;
  //   }

  //   // 3️⃣ Fallback to plain text
  //   const text = clipboard.getData("text/plain");
  //   document.execCommand("insertText", false, text);
  // };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const clipboard = e.clipboardData;
    if (!clipboard) return;

    // 1️⃣ Handle direct image paste (screenshots)
    const items = clipboard.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image")) {
        const file = items[i].getAsFile();
        if (!file) continue;

        const reader = new FileReader();
        reader.onload = (event) => {
          if (!event.target?.result) return;
          insertResizableImage(event.target.result as string);
        };

        reader.readAsDataURL(file);
        return;
      }
    }

    // 2️⃣ Handle HTML paste (Gmail / Websites)
    const html = clipboard.getData("text/html");

    if (html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Remove dangerous elements
      doc.querySelectorAll("script, meta, link, style").forEach((el) => {
        el.remove();
      });

      // Clean attributes but KEEP layout styles
      const cleanNode = (node: Node): Node => {
        if (node.nodeType === Node.TEXT_NODE) {
          return document.createTextNode(node.textContent || "");
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return document.createDocumentFragment();
        }

        const element = node as HTMLElement;

        if (element.tagName === "DIV" && element.classList.contains("resizable-image")) {
          const img = element.querySelector("img");
          if (img) {
            const wrapper = wrapImgElement(img as HTMLImageElement);
            wrapper.setAttribute("data-align", element.getAttribute("data-align") || "left");
            const width = element.style.width;
            if (width) wrapper.style.width = width;
            return wrapper;
          }
        }

        // Allowed tags for email layout
        const allowedTags = [
          "DIV", "P", "BR", "B", "I", "STRONG", "EM",
          "UL", "OL", "LI", "A", "IMG",
          "TABLE", "TBODY", "TR", "TD",
          "THEAD", "TH", "SPAN"
        ];

        if (!allowedTags.includes(element.tagName)) {
          const fragment = document.createDocumentFragment();
          element.childNodes.forEach((child) => {
            fragment.appendChild(cleanNode(child));
          });
          return fragment;
        }

        const el = document.createElement(element.tagName);

        // Copy safe attributes
        for (let attr of element.attributes) {
          const name = attr.name.toLowerCase();
          const value = attr.value;

          // Allow inline style (important for email layout)
          if (name === "style") {
            el.setAttribute("style", value);
          }

          // Allow links
          if (element.tagName === "A" && name === "href") {
            el.setAttribute("href", value);
            el.setAttribute("target", "_blank");
          }

          // Allow image attributes
          if (element.tagName === "IMG") {
            if (["src", "width", "height", "alt"].includes(name)) {
              el.setAttribute(name, value);
            }
          }

          // Allow table attributes
          if (
            ["TABLE", "TD", "TR", "TH"].includes(element.tagName) &&
            ["align", "valign", "colspan", "rowspan", "border", "cellpadding", "cellspacing"].includes(name)
          ) {
            el.setAttribute(name, value);
          }
        }

        if (element.tagName === "IMG") {
          return wrapImgElement(element as HTMLImageElement);
        }

        element.childNodes.forEach((child) => {
          el.appendChild(cleanNode(child));
        });

        return el;
      };

      const cleaned = cleanNode(doc.body);

      const container = document.createElement("div");
      container.appendChild(cleaned);

      document.execCommand("insertHTML", false, container.innerHTML);
      ensureEditableTail();
      placeCaretAtEnd(editorRef.current!);
      setContent(editorRef.current?.innerHTML || "");
      return;
    }

    // 3️⃣ Fallback to plain text
    const text = clipboard.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    let currentWrapper: HTMLElement | null = null;
    let startX = 0;
    let startWidth = 0;

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains("resize-handle")) return;

      e.preventDefault();
      currentWrapper = target.parentElement as HTMLElement;
      startX = e.clientX;
      startWidth = currentWrapper.offsetWidth;

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!currentWrapper || !editorRef.current) return;

      const editorWidth = editorRef.current.clientWidth - 24;
      const nextWidth = Math.max(80, Math.min(startWidth + (e.clientX - startX), editorWidth));
      currentWrapper.style.width = `${nextWidth}px`;
    };

    const onMouseUp = () => {
      if (currentWrapper && editorRef.current) {
        setContent(editorRef.current.innerHTML);
      }
      currentWrapper = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    editor.addEventListener("mousedown", onMouseDown);
    return () => editor.removeEventListener("mousedown", onMouseDown);
  }, []);

  // --- Insert Link ---
  const openLinkPopup = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
    setLinkValue("");
    setLinkTargetBlank(true);
    setShowLinkPopup(true);
  };

  const insertLink = () => {
    if (!linkValue) {
      setShowLinkPopup(false);
      return;
    }

    if (savedRange.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
      }
    }

    document.execCommand("createLink", false, linkValue);

    setShowLinkPopup(false);
    setContent(editorRef.current?.innerHTML || "");
    savedRange.current = null;
  };

  // --- Apply Color ---
  const openColorPopup = () => {
    setShowColorPopup(true);
  };
  const applyColor = () => {
    format("foreColor", colorValue);
    setShowColorPopup(false);
  };

  // --- Font ---
  const openFontPopup = () => {
    // Get current font settings if text is selected
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      const range = sel.getRangeAt(0);
      const parentElement = range.commonAncestorContainer.parentElement;

      if (parentElement) {
        const computedStyle = window.getComputedStyle(parentElement);
        const currentSize = parseInt(computedStyle.fontSize);
        const currentFamily = computedStyle.fontFamily.replace(/['"]/g, '').split(',')[0];

        setFontSizeValue(currentSize.toString());
        setFontFamilyValue(currentFamily);
      }
    }
    setShowFontPopup(true);
  };

  const applyFontSize = (size: string) => {
    setFontSizeValue(size);
    editorRef.current?.focus();

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      // Text is selected - apply to selection only
      document.execCommand("fontSize", false, "7");

      setTimeout(() => {
        if (editorRef.current) {
          const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
          fontElements.forEach((el) => {
            el.removeAttribute("size");
            (el as HTMLElement).style.fontSize = size + "px";
          });
          setContent(editorRef.current?.innerHTML || "");
        }
      }, 0);
    } else {
      // No selection - apply for next typing
      document.execCommand("fontSize", false, "7");

      setTimeout(() => {
        if (editorRef.current) {
          const fontElements = editorRef.current.querySelectorAll('font[size="7"]');
          fontElements.forEach((el) => {
            el.removeAttribute("size");
            (el as HTMLElement).style.fontSize = size + "px";
          });
          setContent(editorRef.current?.innerHTML || "");
        }
      }, 0);
    }
  };

  const applyFontFamily = (family: string) => {
    setFontFamilyValue(family);
    editorRef.current?.focus();
    format("fontName", family);
  };

  const closeFontPopup = () => {
    setShowFontPopup(false);
    editorRef.current?.focus();
  };

  // --- Image Upload ---
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target?.result) {
        insertResizableImage(event.target.result as string);

        setImages((prev) => [
          ...prev,
          { src: event.target.result as string, name: file.name },
        ]);
      }
    };

    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };


  // --- DOCUMENT UPLOAD (PDF, DOCX, etc.) ---
  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target?.result && editorRef.current) {
        const docId = `doc-${Date.now()}`;

        // Create document element to insert in editor
        const docWrapper = document.createElement('div');
        docWrapper.className = 'inline-document-wrapper';
        docWrapper.style.cssText = 'display: inline-block; margin: 8px 4px; padding: 8px 12px; background: #f0f0f0; border: 1px solid #ddd; border-radius: 4px; position: relative;';
        docWrapper.setAttribute('data-doc-id', docId);
        docWrapper.setAttribute('data-doc-name', file.name);
        docWrapper.setAttribute('data-doc-base64', event.target.result as string);

        const icon = document.createElement('span');
        icon.innerHTML = '📎';
        icon.style.marginRight = '8px';

        const fileName = document.createElement('span');
        fileName.textContent = file.name;
        fileName.style.color = '#0066cc';

        const deleteBtn = document.createElement('button');
        deleteBtn.textContent = '×';
        deleteBtn.className = 'delete-inline-doc';
        deleteBtn.style.cssText = 'margin-left: 8px; background: #ff4444; color: white; border: none; border-radius: 50%; width: 20px; height: 20px; cursor: pointer; font-size: 14px; line-height: 1;';

        deleteBtn.addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          docWrapper.remove();
          setContent(editorRef.current?.innerHTML || "");
          // Remove from documents state
          setDocuments(prev => prev.filter(d => d.name !== file.name));
        });

        docWrapper.appendChild(icon);
        docWrapper.appendChild(fileName);
        docWrapper.appendChild(deleteBtn);

        // Append to editor
        editorRef.current.appendChild(docWrapper);
        editorRef.current.appendChild(document.createElement('br')); // Add line break

        // Update content state
        setContent(editorRef.current.innerHTML);

        // Add to documents state with base64
        setDocuments((prev) => [
          ...prev,
          {
            name: file.name,
            url: event.target.result as string // Store base64 directly
          }
        ]);
      }
    };

    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };
  const Restauranttoken = JSON.parse(localStorage.getItem("restaurant"));

  const apiAddress = process.env.REACT_APP_SECRET;


  // const sendContentToBackend = async () => {
  //   if (!subject) {
  //     toast.error("Subject is required");
  //     return;
  //   }

  //   try {
  //     setLoading(true);

  //     // Get full HTML content from editor
  //     let finalBody = editorRef.current?.innerHTML || "";

  //     console.log("=== Before Processing ===");
  //     console.log(finalBody);

  //     // Process inline documents - convert to proper download links
  //     const tempDiv = document.createElement('div');
  //     tempDiv.innerHTML = finalBody;

  //     // Find all document wrappers and convert to download links
  //     const docWrappers = tempDiv.querySelectorAll('.inline-document-wrapper');
  //     docWrappers.forEach((wrapper) => {
  //       const docName = wrapper.getAttribute('data-doc-name');
  //       const docBase64 = wrapper.getAttribute('data-doc-base64');

  //       if (docName && docBase64) {
  //         const link = document.createElement('a');
  //         link.href = docBase64;
  //         link.download = docName;
  //         link.textContent = `📎 ${docName}`;
  //         link.style.cssText = 'color: #0066cc; text-decoration: underline; margin: 0 8px;';

  //         wrapper.replaceWith(link);
  //       }
  //     });

  //     finalBody = tempDiv.innerHTML;

  //     console.log("=== After Processing ===");
  //     console.log(finalBody);
  //     console.log("========================");

  //     const payload = {
  //       restaurantId: Restauranttoken?.id || "",
  //       subject: `${restaurantName}: ${subject}`,
  //       body: finalBody, // Contains images + documents as download links
  //       status: 1,
  //       creationId: "user-001",
  //     };

  //     const response = await axios.post(
  //       `${apiAddress}/api/SubSection/AddEmail`,
  //       payload
  //     );

  //     console.log("Response:", response.data);
  //     toast.success(response.data?.message || "Content Sent Successfully");

  //     setSubject("");
  //     setContent("");
  //     if (editorRef.current) {
  //       editorRef.current.innerHTML = "";
  //     }
  //     setImages([]);
  //     setDocuments([]);

  //   } catch (error: any) {
  //     console.error("Error sending content:", error);
  //     toast.error(error.response?.data?.message || "Failed to send content");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const prepareEmailHtml = async (rawHtml: string): Promise<string> => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rawHtml;

    tempDiv.querySelectorAll(".resize-handle").forEach((el) => el.remove());
    tempDiv.querySelectorAll(".resizable-image.is-selected").forEach((el) => {
      el.classList.remove("is-selected");
    });

    const wrappers = Array.from(tempDiv.querySelectorAll(".resizable-image"));
    for (const wrapper of wrappers) {
      const htmlWrapper = wrapper as HTMLElement;
      const img = htmlWrapper.querySelector("img");
      if (!img) {
        wrapper.remove();
        continue;
      }

      if (img.src.startsWith("data:image")) {
        const file = base64ToFile(img.src, `email-image-${Date.now()}.png`);
        const uploadedUrls = await uploadOneFiles(file);
        if (uploadedUrls.length > 0) {
          img.src = uploadedUrls[0];
        }
      }

      const align = (htmlWrapper.getAttribute("data-align") || "left") as ImageAlign;
      const width = getImageWrapperWidth(htmlWrapper, img);

      const table = document.createElement("table");
      table.setAttribute("role", "presentation");
      table.setAttribute("cellpadding", "0");
      table.setAttribute("cellspacing", "0");
      table.setAttribute("border", "0");
      table.setAttribute("width", "100%");
      table.setAttribute("style", "margin:10px 0;border-collapse:collapse;");

      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.setAttribute("align", align);

      const emailImg = document.createElement("img");
      emailImg.src = img.src;
      emailImg.alt = img.alt || "";
      emailImg.setAttribute("width", String(width));
      emailImg.setAttribute(
        "style",
        `width:${width}px;max-width:100%;height:auto;display:block;border:0;` +
          (align === "center"
            ? "margin:0 auto;"
            : align === "right"
              ? "margin:0 0 0 auto;"
              : "margin:0;")
      );

      td.appendChild(emailImg);
      tr.appendChild(td);
      table.appendChild(tr);
      htmlWrapper.replaceWith(table);
    }

    const remainingImages = tempDiv.querySelectorAll("img");
    for (let i = 0; i < remainingImages.length; i++) {
      const img = remainingImages[i];

      if (img.src.startsWith("data:image")) {
        const file = base64ToFile(img.src, `email-image-${Date.now()}-${i}.png`);
        const uploadedUrls = await uploadOneFiles(file);
        if (uploadedUrls.length > 0) {
          img.src = uploadedUrls[0];
        }
      }

      if (!img.getAttribute("style")) {
        img.setAttribute(
          "style",
          "max-width:100%;height:auto;display:block;margin:10px 0;"
        );
      }
    }

    return tempDiv.innerHTML;
  };

  const sendContentToBackend = async () => {
    if (!subject) {
      toast.error("Subject is required");
      return;
    }

    try {
      setLoading(true);

      const finalBody = await prepareEmailHtml(editorRef.current?.innerHTML || "");

      const payload = {
        restaurantId: Restauranttoken?.id || "",
        subject: `${restaurantName}: ${subject}`,
        body: finalBody,
        status: 1,
        creationId: "user-001",
      };

      await axios.post(
        `${apiAddress}/api/SubSection/AddEmail`,
        payload
      );

      toast.success("Content Sent Successfully");

    } catch (error: any) {
      toast.error("Failed to send content");
    } finally {
      setLoading(false);
      setSubject("");
      setContent("");
      clearSelectedImage();
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }
      setImages([]);
      setDocuments([]);
    }
  };

  return (
    <div className="editor-wrapper">
      <div className="editor-container">

        <h2 className="editor-title">Compose Message</h2>

        <input
          type="text"
          placeholder="Subject"
          className="subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={() => format("bold")}><Bold size={18} /></button>
          <button onClick={() => format("italic")}><Italic size={18} /></button>
          <button onClick={() => format("underline")}><Underline size={18} /></button>
          <button onClick={() => format("insertUnorderedList")}><List size={18} /></button>
          <button onClick={() => format("insertOrderedList")}><ListOrdered size={18} /></button>

          <button onClick={openLinkPopup}><LinkIcon size={18} /></button>
          <button onClick={openColorPopup}><Palette size={18} /></button>
          <button onClick={openFontPopup}><ALargeSmall size={18} /></button>

          <div className="toolbar-group">
            <span className="toolbar-group-label">Text align</span>
            <div className="toolbar-group-buttons">
              <button type="button" title="Align text left" onClick={() => format("justifyLeft")}>
                <AlignLeft size={18} />
              </button>
              <button type="button" title="Align text center" onClick={() => format("justifyCenter")}>
                <AlignCenter size={18} />
              </button>
              <button type="button" title="Align text right" onClick={() => format("justifyRight")}>
                <AlignRight size={18} />
              </button>
              <button type="button" title="Justify text" onClick={() => format("justifyFull")}>
                <AlignJustify size={18} />
              </button>
            </div>
          </div>

          <div className="toolbar-group toolbar-group--image">
            <span className="toolbar-group-label">Image</span>
            <div className="toolbar-group-buttons">
              <label className="image-upload" title="Upload image">
                <ImageIcon size={18} />
                <input type="file" accept="image/*" onChange={handleImageUpload} />
              </label>
              <button
                type="button"
                className="toolbar-image-align-btn"
                title="Align selected image left"
                onClick={() => alignSelectedImage("left")}
              >
                <ImageIcon size={14} />
                <AlignLeft size={14} />
              </button>
              <button
                type="button"
                className="toolbar-image-align-btn"
                title="Align selected image center"
                onClick={() => alignSelectedImage("center")}
              >
                <ImageIcon size={14} />
                <AlignCenter size={14} />
              </button>
              <button
                type="button"
                className="toolbar-image-align-btn"
                title="Align selected image right"
                onClick={() => alignSelectedImage("right")}
              >
                <ImageIcon size={14} />
                <AlignRight size={14} />
              </button>
            </div>
          </div>

          {/* Document Upload */}
          <label className="image-upload" title="Upload Document">
            <FileIcon size={18} />
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.ppt,.pptx"
              onChange={handleDocumentUpload}
            />
          </label>
        </div>

        {/* Editor */}
        <div
          ref={editorRef}
          contentEditable
          className="editor-area"
          onInput={handleInput}
          onPaste={handlePaste}
          onClick={handleEditorClick}
          onKeyDown={handleEditorKeyDown}
          suppressContentEditableWarning
        ></div>

        {/* Image Preview */}
        {images.length > 0 && (
          <div className="image-preview-box">
            {images.map((img, i) => (
              <div key={i} className="image-preview">
                <img src={img.src} />
                <button onClick={() => removeImage(i)}><Trash2 size={14} /></button>
                <p>{img.name}</p>
              </div>
            ))}
          </div>
        )}

        {/* Document Preview */}
        {documents.length > 0 && (
          <div className="document-preview-box">
            {documents.map((doc, i) => (
              <div key={i} className="document-preview">
                <FileIcon size={18} />
                <a href={doc.url} target="_blank">{doc.name}</a>
                <button onClick={() => removeDocument(i)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="actions">
          <button className="send-btn" onClick={sendContentToBackend}>
            <Send size={16} /> Send
          </button>

          <button className="save-btn"><Save size={16} /> Save Draft</button>
          <button className="cancel-btn"><Trash2 size={16} /> Cancel</button>
        </div>

        {/* Preview */}
        {/* <div className="preview-section">
          <h3><Eye size={18} /> Preview</h3>

          <p><strong>Subject:</strong> {subject || "No subject"}</p>

          <div
            className="preview-content"
            dangerouslySetInnerHTML={{
              __html: content || "<em>Start typing...</em>",
            }}
          />
        </div> */}

        {/* LINK MODAL */}
        {showLinkPopup && (
          <div className="editor-modal-backdrop">
            <div className="editor-modal">
              <h3>Insert Link</h3>
              <input
                type="url"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
                className="editor-modal-input"
              />
              <label>
                <input
                  type="checkbox"
                  checked={linkTargetBlank}
                  onChange={(e) => setLinkTargetBlank(e.target.checked)}
                />
                Open in new tab
              </label>
              <div className="editor-modal-actions">
                <button onClick={() => setShowLinkPopup(false)}>Cancel</button>
                <button onClick={insertLink}>Insert</button>
              </div>
            </div>
          </div>
        )}

        {/* COLOR MODAL */}
        {showColorPopup && (
          <div className="editor-modal-backdrop">
            <div className="editor-modal">
              <h3>Choose Text Color</h3>
              <input
                type="color"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
              />
              <div className="editor-modal-actions">
                <button onClick={() => setShowColorPopup(false)}>Cancel</button>
                <button onClick={applyColor}>Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* FONT MODAL */}
        {showFontPopup && (
          <div className="editor-modal-backdrop" onClick={closeFontPopup}>
            <div className="editor-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Font Settings</h3>

              <label>Font Family</label>
              <select
                value={fontFamilyValue}
                onChange={(e) => applyFontFamily(e.target.value)}
                className="editor-modal-input"
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Georgia">Georgia</option>
                <option value="Verdana">Verdana</option>
                <option value="Poppins">Poppins</option>
                <option value="Roboto">Roboto</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Lato">Lato</option>
                <option value="Ubuntu">Ubuntu</option>
                <option value="Nunito">Nunito</option>
              </select>

              <label>Font Size</label>
              <select
                value={fontSizeValue}
                onChange={(e) => applyFontSize(e.target.value)}
                className="editor-modal-input"
              >
                {[8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 36, 40, 44, 48, 54, 60, 66, 72, 80, 88, 96].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <div className="editor-modal-actions">
                <button onClick={closeFontPopup}>Done</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}